// tamagui-ignore
import type { DPDay, DPDayInteger } from '@rehookify/datepicker'
import { useDatePickerContext } from '@rehookify/datepicker'
import { ChevronLeft, ChevronRight } from '@tamagui/lucide-icons'
import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, Button, SizableText, View, YStack } from 'tamagui'

import { useDateAnimation } from './common/datePickerUtils'
import {
  DatePicker,
  DatePickerInput,
  HeaderTypeProvider,
  MonthPicker,
  YearPicker,
  YearRangeSlider,
  swapOnClick,
  useHeaderType,
} from './common/dateparts'
import { type DatePickerConfig, type DatePickerExampleProps, type DatePickerProps } from './types'

function CalendarHeader() {
  const {
    data: { calendars } = { calendars: [] },
    propGetters: { subtractOffset },
  } = useDatePickerContext()
  const { type: header, setHeader } = useHeaderType()
  const { year, month } = calendars?.[0] || {}

  if (header === 'year') {
    return <YearRangeSlider />
  }

  if (header === 'month') {
    return (
      <SizableText width="100%" ta="center" userSelect="auto" tabIndex={0} size="$8">
        Select a month
      </SizableText>
    )
  }

  return (
    <View
      flexDirection="row"
      width="100%"
      height={50}
      alignItems="center"
      justifyContent="space-between"
    >
      <Button
        circular
        unstyled
        padding="$2.5"
        cursor="pointer"
        size="$4"
        {...(subtractOffset ? swapOnClick(subtractOffset({ months: 1 })) : {})}
      >
        <Button.Icon scaleIcon={1.5}>
          <ChevronLeft />
        </Button.Icon>
      </Button>
      <View flexDirection="column" height={50} alignItems="center">
        <SizableText
          onPress={() => setHeader('year')}
          userSelect="auto"
          tabIndex={0}
          size="$4"
          cursor="pointer"
          color="$color11"
          hoverStyle={{
            color: '$color12',
          }}
        >
          {year}
        </SizableText>
        <SizableText
          onPress={() => setHeader('month')}
          userSelect="auto"
          cursor="pointer"
          tabIndex={0}
          size="$6"
          color="$color12"
          fontWeight="600"
          lineHeight={24}
          hoverStyle={{
            color: '$color9',
          }}
        >
          {month}
        </SizableText>
      </View>
      <Button
        circular
        size="$4"
        {...swapOnClick(subtractOffset({ months: -1 }))}
        unstyled
        padding="$2.5"
        cursor="pointer"
        alignSelf="center"
      >
        <Button.Icon scaleIcon={1.5}>
          <ChevronRight />
        </Button.Icon>
      </Button>
    </View>
  )
}

function DayPicker() {
  const {
    data: { calendars, weekDays },
    propGetters: { dayButton },
  } = useDatePickerContext()

  const shortWeekDays = [
    { key: 'sun', label: 'S' },
    { key: 'mon', label: 'M' },
    { key: 'tue', label: 'T' },
    { key: 'wed', label: 'W' },
    { key: 'thu', label: 'T' },
    { key: 'fri', label: 'F' },
    { key: 'sat', label: 'S' },
  ]

  const { days } = calendars[0]

  // if the last week is not in the current month, remove it
  //console.log("day!!!",days[35])
  if (days[35] && days[35].inCurrentMonth === false) {
    days.splice(35)
  }

  //console.log('DayPicker: days', days)
  //console.log('Calendar: calendars', calendars)

  const { prevNextAnimation, prevNextAnimationKey } = useDateAnimation({
    listenTo: 'month',
  })

  // divide days array into sub arrays that each has 7 days, for better stylings
  const subDays = useMemo(
    () =>
      days.reduce((acc, day, i) => {
        //if (i > 30 && i % 7 === 0 && day.inCurrentMonth === false) {
        //return
        //}

        if (i % 7 === 0) {
          acc.push([])
        }
        // if day starts with 0, remove it
        if (day.day.startsWith('0')) {
          day.day = day.day.toString().slice(1)
        }

        acc[acc.length - 1].push(day)
        return acc
      }, [] as DPDay[][]),
    [days]
  )

  return (
    <AnimatePresence key={prevNextAnimationKey}>
      <View animation="medium" {...prevNextAnimation()}>
        <View flexDirection="row" gap="$1">
          {shortWeekDays.map(({ key, label }) => (
            <SizableText key={key} ta="center" width={44} size="$4">
              {label}
            </SizableText>
          ))}
        </View>
        <View flexDirection="column" gap="$0.75" flexWrap="wrap" alignItems="center">
          {subDays.map((days) => {
            return (
              <View flexDirection="row" key={days[0].$date.toString()} gap="$0.75">
                {days.map((d) => (
                  <Button
                    key={d.$date.toString()}
                    chromeless
                    circular
                    padding={0}
                    width={44}
                    {...swapOnClick(dayButton(d))}
                    backgroundColor={
                      d.selected ? '$accentColor' : d.now ? '$color5' : 'transparent'
                    }
                    //themeInverse={d.selected}
                    hoverStyle={
                      d.selected ? { backgroundColor: '$color9' } : { backgroundColor: '$color4' }
                    }
                    borderWidth={0}
                    //disabled={!d.inCurrentMonth}
                  >
                    <Button.Text
                      color={d.selected ? '$color1' : d.inCurrentMonth ? '$color12' : '$color5'}
                    >
                      {d.day}
                    </Button.Text>
                  </Button>
                ))}
              </View>
            )
          })}
        </View>
      </View>
    </AnimatePresence>
  )
}

function DatePickerBody() {
  const [header, setHeader] = useState<'day' | 'month' | 'year'>('day')

  return (
    <HeaderTypeProvider type={header} setHeader={setHeader}>
      <View flexDirection="column" alignItems="center" gap="$2.5" maxWidth={325}>
        <CalendarHeader />
        {header === 'month' && <MonthPicker onChange={() => setHeader('day')} />}
        {header === 'year' && <YearPicker onChange={() => setHeader('day')} />}
        {header === 'day' && <DayPicker />}
      </View>
    </HeaderTypeProvider>
  )
}

/** ------ EXAMPLE ------ */
export function DatePickerExample({
  disabled,
  placeholderTextColor,
  value,
  onChangeText,
  onBlur,
  ref,
  placeholder,
  id,
  ...props
}: {
  disabled: boolean
  placeholderTextColor?: string
  value: string | undefined
  onChangeText: (dateValue: string) => void
  onBlur: () => void
  ref: React.RefObject<HTMLInputElement>
  placeholder?: string
  id: string
  [key: string]: any
}) {
  const [selectedDates, onDatesChange] = useState<Date[]>([])
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setOpen(false)
  }, [selectedDates])

  const datePickerConfig: {
    selectedDates: Date[]
    onDatesChange: (dates: Date[]) => void
    calendar: {
      startDay: DPDayInteger
    }
  } = {
    selectedDates,
    onDatesChange: (dates) => {
      onDatesChange(dates)
      // Format date using local components to avoid timezone shifting
      onChangeText(dates[0] ? `${dates[0].getFullYear()}-${String(dates[0].getMonth() + 1).padStart(2, '0')}-${String(dates[0].getDate()).padStart(2, '0')}` : '')
    },
    calendar: {
      startDay: 0,
    },
  }
  return (
    <DatePicker keepChildrenMounted open={open} onOpenChange={setOpen} config={datePickerConfig}>
      <DatePicker.Trigger>
        <DatePickerInput
          placeholder="Select Date"
          value={selectedDates[0]?.toDateString() || ''}
          onReset={() => onDatesChange([])}
          onButtonPress={() => setOpen(true)}
        />
      </DatePicker.Trigger>
      <DatePicker.Content>
        <DatePicker.Content.Arrow />
        <DatePickerBody />
      </DatePicker.Content>
    </DatePicker>
  )
}

DatePickerExample.fileName = 'DatePicker'

// Add this custom date picker component at the bottom of the file
export function CustomDatePicker({
  value,
  onChange,
  placeholder,
  triggerComponent: TriggerComponent,
  footer: Footer,
  popoverOffset,
  ...props
}: {
  value: string | null
  onChange: (date: string | null) => void
  placeholder?: string
  triggerComponent: React.ComponentType<{
    value: string | null
    placeholder: string
    onPress: () => void
  }>
  footer?: React.ComponentType<{
    onClose: () => void
    onClear: () => void
  }>
  popoverOffset?: number
  [key: string]: any
}) {
  // Create a function to consistently format dates
  const formatDateToLocalTime = (dateString: string | null): Date[] => {
    if (!dateString) return []
    const [year, month, day] = dateString.split('-').map(Number)
    // Create date in local timezone
    const localDate = new Date(year, month - 1, day, 12) // Add noon hour to avoid timezone issues
    return [localDate]
  }

  const [selectedDates, setSelectedDates] = useState<Date[]>(() => formatDateToLocalTime(value))
  const [open, setOpen] = useState(false)

  // Update selectedDates when value prop changes
  useEffect(() => {
    setSelectedDates(formatDateToLocalTime(value))
  }, [value])

  //console.log('CustomDatePicker: value', value)
  //console.log('CustomDatePicker: placeholder', placeholder)
  //console.log('CustomDatePicker: selectedDates', selectedDates)

  useEffect(() => {
    setOpen(false)
  }, [selectedDates])

  const datePickerConfig: {
    selectedDates: Date[]
    onDatesChange: (dates: Date[]) => void
    calendar: {
      startDay: DPDayInteger
    }
  } = {
    selectedDates,
    onDatesChange: (dates) => {
      console.log('CustomDatePicker: onDatesChange: dates', dates)
      setSelectedDates(dates)
      if (dates[0]) {
        // Format date using local components to avoid timezone shifting
        const year = dates[0].getFullYear()
        const month = String(dates[0].getMonth() + 1).padStart(2, '0')
        const day = String(dates[0].getDate()).padStart(2, '0')
        const formattedDate = `${year}-${month}-${day}`
        onChange(formattedDate)
      } else {
        onChange(null)
      }
    },
    calendar: {
      startDay: 0,
    },
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleClear = () => {
    setSelectedDates([])
    onChange(null)
    setOpen(false)
  }

  return (
    <DatePicker
      keepChildrenMounted
      open={open}
      onOpenChange={setOpen}
      config={datePickerConfig}
      popoverOffset={popoverOffset}
    >
      <DatePicker.Trigger>
        <TriggerComponent
          value={value}
          placeholder={placeholder || 'Select Date'}
          onPress={() => setOpen(true)}
        />
      </DatePicker.Trigger>
      <DatePicker.Content
        borderWidth={1}
        borderColor="$color6"
        shadowOffset={{ width: 0, height: 0 }}
        shadowRadius={6}
        shadowColor="$color12"
        shadowOpacity={0.2}
      >
        <YStack>
          <DatePickerBody />
          {Footer && <Footer onClose={handleClose} onClear={handleClear} />}
        </YStack>
      </DatePicker.Content>
    </DatePicker>
  )
}

CustomDatePicker.fileName = 'CustomDatePicker'
